/**
 * Create — show game info and create a room.
 *
 * Flow:
 *  1. Arrives with ?gameId= set by the Home page game picker
 *  2. POST /api/rooms → receive { roomId, token }
 *  3. Redirect to /room/:roomId
 *
 * Display name is taken from the player store (set via sidebar profile modal).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button, Card } from '@playground/ui';
import { usePlayerStore } from '@/store/player.js';
import { useRoomStore } from '@/store/room.js';
import { getGameMeta } from '@/platform/game-registry.js';
import { isEmbedMode } from '@/platform/embed.js';
import { Swords, Users } from 'lucide-react';
import { apiFetch, apiFetchWithToken } from '@/platform/api.js';
import { isSelfContainedGame } from '@/platform/games.js';

interface CreateRoomResponse {
  roomId: string;
  token: string;
}

interface VerifyResponse {
  token: string;
  playerId: string;
  displayName: string;
}

export function Create() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embed = isEmbedMode();

  const gameId = searchParams.get('gameId') ?? '';
  const gameMeta = gameId ? getGameMeta(gameId) : null;
  const selfContained = isSelfContainedGame(gameId);

  const { playerId, displayName, setToken } = usePlayerStore();
  const { setRoom } = useRoomStore();

  // Signed-URL identity verify (SC mobile app deep-link).
  // When URL has userId+displayName+ts+hash, verify on server and set
  // identity, then render the lobby CTA screen so the user picks
  // CREATE ROOM or JOIN PUBLIC ROOM. No room is auto-created.
  const [exchanging, setExchanging] = useState(
    Boolean(
      searchParams.get('userId') &&
      searchParams.get('hash') &&
      searchParams.get('ts'),
    ),
  );

  useEffect(() => {
    const userId = searchParams.get('userId');
    const urlDisplayName = searchParams.get('displayName');
    const ts = searchParams.get('ts');
    const hash = searchParams.get('hash');
    const urlGameId = searchParams.get('gameId');

    // Optimistically overwrite the auto-generated random uuid with the URL identity.
    // localStorage now matches what's in the URL even before verify completes.
    if (userId) usePlayerStore.getState().setPlayerId(userId);
    if (urlDisplayName) usePlayerStore.getState().setDisplayName(urlDisplayName);

    if (!userId || !urlDisplayName || !ts || !hash || !urlGameId) {
      setExchanging(false);
      return;
    }

    const VERIFY_MAX_ATTEMPTS = 3;
    const VERIFY_RETRY_DELAY_MS = 500;
    const verifyBody = JSON.stringify({
      userId,
      displayName: urlDisplayName,
      gameId: urlGameId,
      ts: Number(ts),
      hash,
    });

    void (async () => {
      let lastErr: unknown = null;
      for (let attempt = 1; attempt <= VERIFY_MAX_ATTEMPTS; attempt++) {
        try {
          const res = await apiFetch('/api/auth/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: verifyBody,
          });

          // 404 → endpoint missing on server (old deploy). Retry, then bail to native.
          if (res.status === 404) {
            lastErr = new Error('Verify endpoint not found (404)');
            if (attempt < VERIFY_MAX_ATTEMPTS) {
              await new Promise((r) => setTimeout(r, VERIFY_RETRY_DELAY_MS));
              continue;
            }
            // All retries exhausted on 404 — tell native app to close the webview.
            window.ReactNativeWebView?.postMessage(JSON.stringify({
              type: 'NAVIGATE_TO_LOBBY',
              reason: 'verify_endpoint_not_found',
            }));
            setError('Service unavailable. Returning to lobby…');
            setExchanging(false);
            return;
          }

          if (!res.ok) {
            const body = await res.json().catch(() => ({})) as { error?: string };
            throw new Error(body.error ?? `Server error (${res.status})`);
          }

          const data = (await res.json()) as VerifyResponse;
          usePlayerStore.getState().setPlayerId(data.playerId);
          usePlayerStore.getState().setDisplayName(data.displayName);
          // Identity-only JWT — lets the client call JWT-gated reads
          // (e.g. /api/credits/me) before a room exists. Replaced by a
          // room JWT later when CREATE ROOM / JOIN succeeds.
          setToken(data.token);

          // Strip signed deep-link params from URL after successful verify.
          // Keeps gameId + embed so the CTA view still renders correctly.
          const clean = new URL(window.location.href);
          clean.searchParams.delete('userId');
          clean.searchParams.delete('displayName');
          clean.searchParams.delete('ts');
          clean.searchParams.delete('hash');
          window.history.replaceState({}, '', clean.toString());

          setExchanging(false);
          return;
        } catch (err) {
          lastErr = err;
          // Non-404 errors: surface immediately, no retry.
          break;
        }
      }
      setError(lastErr instanceof Error ? lastErr.message : 'Failed to authenticate');
      setExchanging(false);
    })();
  // Run once on mount with the params at first render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [language, setLanguage] = useState('en');
  const [difficulty, setDifficulty] = useState('mixed');
  const [isPrivate, setIsPrivate] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteName, setSiteName] = useState('Playground');
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const tok = usePlayerStore.getState().token;
    if (!tok) return;
    apiFetchWithToken('/api/games', tok)
      .then((r) => r.json() as Promise<{ siteName?: string }>)
      .then((d) => { if (d.siteName) setSiteName(d.siteName); })
      .catch(() => {});
  }, []);

  // Redirect to home if no game was selected
  useEffect(() => {
    if (!gameId) void navigate('/');
  }, [gameId, navigate]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const effectiveName = displayName.trim() || (embed ? 'Player' : '');
    if (!effectiveName) {
      void navigate('/');
      return;
    }
    if (!gameId) {
      void navigate('/');
      return;
    }

    setLoading(true);
    try {
      const res = await apiFetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gameId,
          displayName: effectiveName,
          playerId,
          ...(gameMeta?.isWordGame ? { language, difficulty } : {}),
          isPrivate,
        }),
      });

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `Server error ${res.status}`);
      }

      const data = (await res.json()) as CreateRoomResponse;

      setToken(data.token);

      setRoom({
        roomId: data.roomId,
        gameId,
        players: [],
        status: 'waiting',
      });

      window.ReactNativeWebView?.postMessage(JSON.stringify({
        type: 'ROOM_CREATED',
        roomId: data.roomId,
        gameId,
      }));

      void navigate(`/room/${data.roomId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room.');
    } finally {
      setLoading(false);
    }
  }


  // While exchanging signed URL params, render a loading splash and skip the picker.
  if (exchanging && !error) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-indigo-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Starting game…</p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Embed mode: dark "battle" design matching the mobile app's visual language.
  // No header (native nav provides back), no word-game options, two CTA buttons.
  // ---------------------------------------------------------------------------
  if (embed) {
    return (
      <div className="min-h-dvh flex flex-col" style={{ background: 'linear-gradient(to bottom, #060D1A, #0D1520)' }}>

        {/* ── Back button ── */}
        <div className="px-5 pt-14 pb-2">
          <button
            type="button"
            onClick={() => window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'NAVIGATE_TO_LOBBY' }))}
            className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-white transition-colors active:scale-95"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            <span className="text-sm font-medium">Back</span>
          </button>
        </div>

        {/* ── Top spacer ── */}
        <div className="h-8" />

        {/* ── Art / title placeholder ── */}
        <div className="flex flex-col items-center px-6 pb-4">
          {/* Square game artwork, 288px at most. A hard w-72 plus the px-6
              gutters came to 336px, which overflowed a 320px-wide phone — cap
              against the viewport instead so it always fits. */}
          <div className="aspect-square w-[min(18rem,78vw)] rounded-3xl overflow-hidden shadow-2xl shadow-black/60">
            {!imgError ? (
              <img
                src={`/thumbnails/${gameId}.png`}
                alt={gameMeta?.name ?? ''}
                onError={() => setImgError(true)}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-blue-700 flex items-center justify-center">
                <span className="text-8xl select-none">🎮</span>
              </div>
            )}
          </div>
          {/* Title placeholder — replace with custom font/asset later */}
          <h1 className="-mt-2 relative z-10 text-4xl font-black text-white text-center tracking-tight"
            style={{ textShadow: '0 2px 16px rgba(0,0,0,0.6)' }}>
            {gameMeta?.name ?? 'Game'}
          </h1>
        </div>

        {/* ── Elastic spacer — absorbs remaining space so card stays pinned ── */}
        <div className="flex-1" />

        {/* ── Floating action card ── */}
        <div className="mx-5 mb-24 rounded-3xl bg-gray-900 px-7 pt-9 pb-10 shadow-2xl">

          {/* Heading */}
          <h2 className="text-2xl font-black uppercase mb-3 leading-tight tracking-wide text-center">
            <span className="text-white">READY FOR </span>
            <span className="text-cyan-400">BATTLE?</span>
          </h2>

          {/* Subtitle. `whitespace-nowrap` used to force horizontal overflow of
              the whole card on narrow phones; let the text wrap instead. */}
          <p className="mb-5 text-center text-sm leading-relaxed text-pretty text-gray-500 dark:text-gray-400">
            {selfContained
              ? 'Tap start to enter the arena. Create or join a race from inside the game.'
              : 'Join an arena or create your own neon grid. Stake your credits and dominate the board.'}
          </p>

          {error && (
            <p className="mb-6 rounded-xl bg-red-900/30 px-4 py-3 text-sm text-red-400 text-center" role="alert">
              {error}
            </p>
          )}

          <div className="space-y-4">
            {/* Primary CTA. For self-contained games (horse-racing) the room
                create/join lives INSIDE the game, so we show a single START
                GAME button that enters the game's own home screen. Other games
                keep the playground's CREATE ROOM + JOIN PUBLIC ROOM flow. */}
            <form onSubmit={(e) => void handleCreate(e)}>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-2xl bg-cyan-400 text-black font-black text-sm uppercase tracking-widest flex items-center justify-center gap-1.5 disabled:opacity-50 active:scale-[0.97] transition-transform duration-100"
              >
                {loading ? (
                  <div className="h-5 w-5 rounded-full border-2 border-black/30 border-t-black animate-spin" />
                ) : (
                  <>
                    <Swords aria-hidden="true" className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                    {selfContained ? 'START GAME' : 'CREATE ROOM'}
                  </>
                )}
              </button>
            </form>

            {/* Secondary CTA — JOIN PUBLIC ROOM (hidden for self-contained games) */}
            {!selfContained && (
              <button
                type="button"
                onClick={() => void navigate('/join')}
                className="w-full py-4 rounded-2xl bg-gray-700/80 text-white font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-3 active:scale-[0.97] transition-transform duration-100"
              >
                <Users className="h-5 w-5 shrink-0" strokeWidth={2.2} />
                JOIN PUBLIC ROOM
              </button>
            )}
          </div>
        </div>

      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Default (desktop / non-embed) layout
  // ---------------------------------------------------------------------------
  return (
    <div>
      {/* Top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between pl-[max(1rem,var(--safe-l))] pr-[max(1rem,var(--safe-r))] pt-[calc(0.75rem+var(--safe-t))] pb-3 bg-white/90 dark:bg-gray-950/90 backdrop-blur border-b border-gray-100 dark:border-gray-800">
        <Link to="/" className="flex items-center gap-2">
          <img src="/favicon.png" alt="" className="w-7 h-7 rounded-md" />
          <span className="text-base font-extrabold tracking-tight text-indigo-700 dark:text-indigo-400">{siteName}</span>
        </Link>
        <Link to="/" className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline">&larr; Back</Link>
      </header>

      <main className="mx-auto max-w-lg px-4 py-8">
        <Card>
          {/* Game thumbnail */}
          <div className="relative overflow-hidden rounded-xl mb-4" style={{ height: 180 }}>
            {!imgError ? (
              <img
                src={`/thumbnails/${gameId}.png`}
                alt={gameMeta?.name ?? ''}
                onError={() => setImgError(true)}
                className="block w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                <span className="text-6xl">🎮</span>
              </div>
            )}
          </div>

          {/* Game name + description */}
          <h1 className="text-xl font-black text-gray-900 dark:text-gray-100 mb-1">
            {gameMeta?.name ?? 'Create a Room'}
          </h1>
          {gameMeta?.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 leading-snug">
              {gameMeta.description}
            </p>
          )}

          <form onSubmit={(e) => void handleCreate(e)} className="space-y-5">
            {/* Language + difficulty selectors — only for word games */}
            {gameMeta?.isWordGame && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="language" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Language
                  </label>
                  <select
                    id="language"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="difficulty" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Difficulty
                  </label>
                  <select
                    id="difficulty"
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                  >
                    <option value="mixed">Mixed</option>
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>
            )}

            {/* Private room */}
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Private room{' '}
                <span className="text-gray-500 dark:text-gray-400">(not listed in the public lobby)</span>
              </span>
            </label>

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" fullWidth loading={loading}>
              Create Room
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
